import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import RepairRequestStatusBadge from '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestStatusBadge';
import { mapRepairRequestMeta } from '#/modules/crmMgmt/aftersales/repairRequest/helper/mapRepairRequestMeta';

function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

function HistoryField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-anywhere text-gray-900">{value}</p>
    </div>
  );
}

HistoryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

function RepairRequestHero({ meta, actions, compact = false }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <h1
            className={cn(
              'font-bold text-gray-900',
              compact ? 'text-xl' : 'text-2xl',
            )}
          >
            {meta.code}
          </h1>
          {meta.status ? <RepairRequestStatusBadge status={meta.status} /> : null}
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

RepairRequestHero.propTypes = {
  meta: PropTypes.shape({
    code: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  actions: PropTypes.node,
  compact: PropTypes.bool,
};

function RepairRequestInfoGrid({ meta, compact = false }) {
  return (
    <div
      className={cn(
        'mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-slate-200 pt-4',
        !compact && 'sm:grid-cols-2 md:grid-cols-4',
      )}
    >
      <HistoryField label="建立" value={meta.createDate} />
      <HistoryField label="修改" value={meta.reviseDate} />
      <HistoryField label="成立" value={meta.establishedAt} />
      <HistoryField label="完成" value={meta.completedAt} />
      <HistoryField label="報修日" value={meta.repairRequestDate} />
      <HistoryField label="收件日" value={meta.receivedDate} />
      <HistoryField label="客戶" value={displayOrDash(meta.customerName)} />
      <HistoryField label="聯絡人" value={displayOrDash(meta.contactName)} />
      <HistoryField label="電話" value={displayOrDash(meta.phone)} />
      <HistoryField label="傳真" value={displayOrDash(meta.fax)} />
      <HistoryField label="統編" value={displayOrDash(meta.taxId)} />
      <HistoryField label="項目數" value={meta.lineCount} />
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <p className="text-xs text-gray-500">地址</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.address)}
        </p>
      </div>
      <div className={cn(!compact && 'sm:col-span-2 md:col-span-2')}>
        <p className="text-xs text-gray-500">備註</p>
        <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
          {displayOrDash(meta.remark)}
        </p>
      </div>
    </div>
  );
}

RepairRequestInfoGrid.propTypes = {
  meta: PropTypes.shape({
    createDate: PropTypes.string,
    reviseDate: PropTypes.string,
    establishedAt: PropTypes.string,
    completedAt: PropTypes.string,
    repairRequestDate: PropTypes.string,
    receivedDate: PropTypes.string,
    customerName: PropTypes.string,
    contactName: PropTypes.string,
    phone: PropTypes.string,
    fax: PropTypes.string,
    taxId: PropTypes.string,
    address: PropTypes.string,
    lineCount: PropTypes.number,
    remark: PropTypes.string,
  }).isRequired,
  compact: PropTypes.bool,
};

const RepairRequestMainInfoCard = ({
  detail,
  embedded = false,
  nested = false,
  compact = false,
  actions = null,
  containerClassName = '',
}) => {
  const meta = mapRepairRequestMeta(detail);
  const surfacePaddingClassName = compact ? 'p-4' : 'p-5';

  const content = (
    <>
      <RepairRequestHero meta={meta} actions={actions} compact={compact} />
      <RepairRequestInfoGrid meta={meta} compact={compact} />
    </>
  );

  if (nested) {
    return content;
  }

  if (embedded) {
    return (
      <div className={cn('border-b border-slate-200', surfacePaddingClassName)}>
        {content}
      </div>
    );
  }

  return (
    <div className={cn('receipt-card-elevate', containerClassName)}>
      <div className={cn('receipt-card-surface', surfacePaddingClassName)}>
        {content}
      </div>
    </div>
  );
};

RepairRequestMainInfoCard.propTypes = {
  detail: PropTypes.object.isRequired,
  embedded: PropTypes.bool,
  nested: PropTypes.bool,
  compact: PropTypes.bool,
  actions: PropTypes.node,
  containerClassName: PropTypes.string,
};

export default RepairRequestMainInfoCard;
